"use client";

import * as React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "@/components/icons";
import { cn } from "@/lib/utils";
import {
  DIALOG_VARIANT,
  type DialogVariant,
} from "@/components/ui/dialog-variant";


export interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  onConfirm: () => void | boolean | Promise<void | boolean>;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: DialogVariant;
  loadingLabel?: string;
  showIcon?: boolean;
}

function useConfirmAction(
  onConfirm: ConfirmationDialogProps["onConfirm"],
  onOpenChange: ConfirmationDialogProps["onOpenChange"],
) {
  const [isLoading, setIsLoading] = React.useState(false);
  const handleConfirm = async (event: React.MouseEvent) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      if ((await onConfirm()) !== false) onOpenChange(false);
    } finally {
      setIsLoading(false);
    }
  };
  return { isLoading, handleConfirm };
}

function ConfirmLabel({
  isLoading,
  loadingLabel,
  confirmLabel,
}: {
  isLoading: boolean;
  loadingLabel: string;
  confirmLabel: string;
}) {
  if (!isLoading) return <>{confirmLabel}</>;
  return (
    <>
      <Loader2 className="mr-2 size-4 animate-spin" />
      {loadingLabel}
    </>
  );
}

function ConfirmationHeader({
  title,
  description,
  variant,
  showIcon,
}: Pick<ConfirmationDialogProps, "title" | "description"> & {
  variant: DialogVariant;
  showIcon: boolean;
}) {
  const config = DIALOG_VARIANT[variant];
  const Icon = config.icon;
  const text = (
    <>
      <AlertDialogTitle>{title}</AlertDialogTitle>
      <AlertDialogDescription asChild>
        <div className={showIcon ? "mt-2" : undefined}>{description}</div>
      </AlertDialogDescription>
    </>
  );
  return (
    <AlertDialogHeader>
      {showIcon ? (
        <div className="flex items-start gap-4">
          <div className={cn("rounded-full p-2", config.chip)}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1">{text}</div>
        </div>
      ) : (
        text
      )}
    </AlertDialogHeader>
  );
}

export function ConfirmationDialog({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  loadingLabel = "Processing...",
  showIcon = false,
}: ConfirmationDialogProps) {
  const { isLoading, handleConfirm } = useConfirmAction(onConfirm, onOpenChange);
  const config = DIALOG_VARIANT[variant];

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <ConfirmationHeader
          title={title}
          description={description}
          variant={variant}
          showIcon={showIcon}
        />
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            variant={config.buttonVariant}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            <ConfirmLabel
              isLoading={isLoading}
              loadingLabel={loadingLabel}
              confirmLabel={confirmLabel}
            />
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export interface ConfirmDialogState {
  open: boolean;
  title: string;
  description: React.ReactNode;
  variant?: DialogVariant;
  action: () => Promise<void | boolean>;
}

export const defaultConfirmDialogState: ConfirmDialogState = {
  open: false,
  title: "",
  description: "",
  action: async () => {},
};
